import axios from "axios";

export const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// On a 401 (expired 15-min access token), transparently refresh once using the
// 7-day refresh cookie and retry, so long study sessions never break mid-flow.
let refreshPromise = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const url = original.url || "";
    const skip = url.includes("/auth/refresh") || url.includes("/auth/login") || url.includes("/auth/logout");
    if (error.response?.status === 401 && !original._retry && !skip) {
      original._retry = true;
      try {
        refreshPromise = refreshPromise || api.post("/auth/refresh");
        await refreshPromise;
        refreshPromise = null;
        return api(original);
      } catch (e) {
        refreshPromise = null;
      }
    }
    // 402 = a credit / Go Deeper plan limit was hit. Surface a global upgrade
    // nudge at the moment of friction (a component listens for this event).
    if (error.response?.status === 402 && typeof window !== "undefined") {
      const message = formatApiError(error.response?.data?.detail);
      // Derive which feature hit the wall from the request path.
      let feature = "credits";
      if (url.includes("/chat")) feature = "go_deeper";
      else if (url.includes("/sermon")) feature = "study_to_sermon";
      else if (url.includes("/prayer/to-study")) feature = "prayer_to_study";
      // Client-side signal for PostHog (real upgrade demand).
      try {
        window.posthog?.capture?.("limit_hit", { feature, message });
      } catch (e) { /* analytics is best-effort */ }
      // Server-side log for the in-app Growth dashboard (fire-and-forget).
      try {
        api.post("/track/limit-hit", { feature, reason: message }).catch(() => {});
      } catch (e) { /* never block the user flow */ }
      window.dispatchEvent(
        new CustomEvent("openword:upgrade", { detail: { message } })
      );
    }
    return Promise.reject(error);
  }
);
